import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle, AlertCircle, Loader } from "lucide-react";
import { handleAuthCallback } from "../lib/auth";

export default function Confirm() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Check if there's a token in the URL (user clicked the email link)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const type = urlParams.get('type');
    
    if (token) {
      handleConfirmationToken(token, type || '');
    }
  }, []);

  const handleConfirmationToken = async (token: string, type: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Process the authentication callback
      const authResult = await handleAuthCallback(window.location.href);
      
      if (!authResult.success) {
        setError(authResult.error || 'Failed to confirm email');
        return;
      }
      
      setSuccess(true);
      
      // Redirect to home page after a delay
      setTimeout(() => {
        window.location.href = '/?confirmed=true';
      }, 2000);
    } catch (err) {
      console.error('Error handling confirmation token:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-8 text-center">
      <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
        {loading ? (
          <>
            <Loader className="h-16 w-16 text-orange-500 mx-auto mb-6 animate-spin" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Processing Confirmation
            </h1>
            <p className="text-gray-700 text-lg mb-6">
              Please wait while we process your confirmation...
            </p>
          </>
        ) : error ? (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Confirmation Error
            </h1>
            <p className="text-red-600 mb-6">
              {error}
            </p>
            <Link 
              to="/login" 
              className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back to Login
            </Link>
          </>
        ) : success ? (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Confirmation Successful
            </h1>
            <p className="text-gray-700 text-lg mb-6">
              Your email has been confirmed. You will be redirected to login.
            </p>
            <Link 
              to="/login" 
              className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Go to Login
            </Link>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
              <Mail className="h-8 w-8 text-orange-500" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Bestätigung erforderlich
            </h1>
            
            <p className="text-gray-700 text-lg mb-6">
              Bitte überprüfe deine E-Mails und bestätige deinen Account über den Link, den wir dir geschickt haben.
            </p>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 text-left">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    Falls du keine E-Mail erhalten hast, überprüfe auch deinen Spam-Ordner.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col space-y-4">
              <Link 
                to="/login" 
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Zurück zum Login
              </Link>
              
              <p className="text-sm text-gray-500">
                Probleme bei der Bestätigung? <Link to="/login" className="text-orange-500 hover:text-orange-600">Kontaktiere uns</Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}